import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PhotoCamera as PhotoCameraIcon,
} from '@mui/icons-material';
import axios from 'axios';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (
    typeof window !== 'undefined' &&
    (window.location.hostname.includes('ondigitalocean.app') ||
      window.location.hostname.includes('services.knls.in'))
  ) {
    return '/api';
  }
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();

// Distinct vibrant gradient per card (cycles for more programs)
const CARD_GRADIENTS = [
  'linear-gradient(135deg, #4f46e5 0%, #818cf8 100%)',
  'linear-gradient(135deg, #0891b2 0%, #22d3ee 100%)',
  'linear-gradient(135deg, #d97706 0%, #fbbf24 100%)',
  'linear-gradient(135deg, #7c3aed 0%, #c084fc 100%)',
  'linear-gradient(135deg, #059669 0%, #34d399 100%)',
  'linear-gradient(135deg, #db2777 0%, #f472b6 100%)',
  'linear-gradient(135deg, #2563eb 0%, #60a5fa 100%)',
  'linear-gradient(135deg, #ea580c 0%, #fb923c 100%)',
  'linear-gradient(135deg, #0d9488 0%, #2dd4bf 100%)',
  'linear-gradient(135deg, #9333ea 0%, #e879f9 100%)',
];

const emptyForm = {
  id: null,
  program_code: '',
  title: '',
  subtitle: '',
  description: '',
  category: '',
  delivery_mode: 'Offline',
  duration_days: 30,
  fee: 0,
  level: 'Beginner',
  language: 'English',
  seats: 30,
  rating: 4.5,
  is_featured: false,
  tags: '',
  highlights: '',
  syllabus: '',
  status: 'active',
  display_order: 0,
};

const listToText = (value) => (Array.isArray(value) ? value.join('\n') : '');
const textToList = (value) =>
  String(value || '')
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);

const TrainingProgramsManagement = () => {
  const [programs, setPrograms] = useState([]);
  const [campusImages, setCampusImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [uploadingCampusImage, setUploadingCampusImage] = useState(false);

  const loadPrograms = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API_BASE_URL}/training-programs`, { params: { limit: 200 } });
      setPrograms(Array.isArray(response?.data?.programs) ? response.data.programs : []);
    } catch (err) {
      console.error('Failed to load training programs:', err);
      setError('Failed to load training programs');
    } finally {
      setLoading(false);
    }
  };

  const loadCampusImages = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/training-programs/campus-images`);
      setCampusImages(Array.isArray(response?.data?.images) ? response.data.images : []);
    } catch (err) {
      console.error('Failed to load campus images:', err);
      setError('Failed to load campus images');
    }
  };

  useEffect(() => {
    loadPrograms();
    loadCampusImages();
  }, []);

  const openCreate = () => {
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (program) => {
    setForm({
      ...program,
      tags: listToText(program.tags),
      highlights: listToText(program.highlights),
      syllabus: listToText(program.syllabus),
    });
    setDialogOpen(true);
  };

  const saveProgram = async () => {
    const payload = {
      program_code: form.program_code,
      title: form.title,
      subtitle: form.subtitle,
      description: form.description,
      category: form.category,
      delivery_mode: form.delivery_mode,
      duration_days: Number(form.duration_days),
      fee: Number(form.fee),
      level: form.level,
      language: form.language,
      seats: Number(form.seats),
      rating: Number(form.rating),
      is_featured: Boolean(form.is_featured),
      tags: textToList(form.tags),
      highlights: textToList(form.highlights),
      syllabus: textToList(form.syllabus),
      status: form.status,
      display_order: Number(form.display_order),
    };

    try {
      if (form.id) {
        await axios.put(`${API_BASE_URL}/training-programs/${form.id}`, payload);
      } else {
        await axios.post(`${API_BASE_URL}/training-programs`, payload);
      }
      setDialogOpen(false);
      await loadPrograms();
    } catch (err) {
      console.error('Save training program failed:', err);
      setError(err?.response?.data?.message || 'Failed to save training program');
    }
  };

  const deleteProgram = async (programId) => {
    try {
      await axios.delete(`${API_BASE_URL}/training-programs/${programId}`);
      await loadPrograms();
    } catch (err) {
      console.error('Delete training program failed:', err);
      setError(err?.response?.data?.message || 'Failed to delete training program');
    }
  };

  const uploadCampusImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploadingCampusImage(true);
      setError('');
      const formData = new FormData();
      formData.append('image', file);
      formData.append('title', file.name.replace(/\.[^.]+$/, ''));
      formData.append('display_order', String(campusImages.length));

      await axios.post(`${API_BASE_URL}/training-programs/campus-images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      await loadCampusImages();
    } catch (err) {
      console.error('Campus image upload failed:', err);
      setError(err?.response?.data?.message || 'Failed to upload campus image');
    } finally {
      setUploadingCampusImage(false);
      event.target.value = '';
    }
  };

  const deleteCampusImage = async (imageId) => {
    try {
      await axios.delete(`${API_BASE_URL}/training-programs/campus-images/${imageId}`);
      await loadCampusImages();
    } catch (err) {
      console.error('Campus image delete failed:', err);
      setError(err?.response?.data?.message || 'Failed to delete campus image');
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={900}>Training Programs Admin</Typography>
          <Typography variant="body2" color="text.secondary">Manage programs shown on /training-programs page.</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ background: 'linear-gradient(135deg, #4f46e5, #7c3aed)', px: 3, fontWeight: 700 }}>Add Program</Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Alert severity="info" sx={{ mb: 2 }}>Loading programs...</Alert>}

      <Card elevation={0} sx={{ mb: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={1.5} sx={{ mb: 1.5 }}>
            <Box>
              <Typography variant="h6" fontWeight={800}>Campus Gallery Images</Typography>
              <Typography variant="body2" color="text.secondary">
                /training-programs page lo kanipinche photos ni ikkada upload/delete cheyyandi.
              </Typography>
            </Box>
            <Button
              component="label"
              variant="contained"
              startIcon={<PhotoCameraIcon />}
              disabled={uploadingCampusImage}
              sx={{ background: 'linear-gradient(135deg, #0f766e, #14b8a6)', fontWeight: 700 }}
            >
              {uploadingCampusImage ? 'Uploading...' : 'Upload Photo'}
              <input hidden type="file" accept="image/*" onChange={uploadCampusImage} />
            </Button>
          </Stack>

          <Grid container spacing={1.5}>
            {campusImages.length === 0 && (
              <Grid item xs={12}>
                <Alert severity="info">No campus images uploaded yet.</Alert>
              </Grid>
            )}
            {campusImages.map((image) => (
              <Grid item xs={12} sm={6} md={3} key={image.id}>
                <Card elevation={0} sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                  <Box
                    component="img"
                    src={image.image_url}
                    alt={image.image_title || 'Campus Image'}
                    sx={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }}
                  />
                  <Box sx={{ p: 1.2 }}>
                    <Typography variant="body2" fontWeight={700} noWrap>
                      {image.image_title || 'Campus Photo'}
                    </Typography>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.8 }}>
                      <Chip label={`#${image.display_order || 0}`} size="small" />
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => deleteCampusImage(image.id)}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        {programs.map((program, idx) => {
          const gradient = CARD_GRADIENTS[idx % CARD_GRADIENTS.length];
          return (
            <Grid item xs={12} md={6} lg={4} key={program.id}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  overflow: 'hidden',
                  background: gradient,
                  color: '#fff',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                  transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 16px 40px rgba(0,0,0,0.28)',
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                    <Typography variant="overline" sx={{ bgcolor: 'rgba(255,255,255,0.22)', px: 1, py: 0.2, borderRadius: 1, fontWeight: 700, letterSpacing: 1 }}>
                      {program.program_code}
                    </Typography>
                    {program.is_featured && (
                      <Chip label="⭐ Featured" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.28)', color: '#fff', fontWeight: 700, fontSize: '0.7rem' }} />
                    )}
                  </Stack>
                  <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5, lineHeight: 1.3 }}>{program.title}</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.88, mb: 1.5 }}>{program.subtitle}</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.85, mb: 1.5 }}>
                    ₹{Number(program.fee || 0).toLocaleString('en-IN')} &nbsp;•&nbsp; {program.delivery_mode} &nbsp;•&nbsp; {program.duration_days} Days
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" startIcon={<EditIcon />} onClick={() => openEdit(program)}
                      sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.22)', '&:hover': { bgcolor: 'rgba(255,255,255,0.36)' }, fontWeight: 700, borderRadius: 2 }}>
                      Edit
                    </Button>
                    <Button size="small" startIcon={<DeleteIcon />} onClick={() => deleteProgram(program.id)}
                      sx={{ color: '#fff', bgcolor: 'rgba(255,50,50,0.30)', '&:hover': { bgcolor: 'rgba(255,50,50,0.48)' }, fontWeight: 700, borderRadius: 2 }}>
                      Delete
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{form.id ? 'Edit Training Program' : 'Create Training Program'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={4}><TextField label="Program Code" fullWidth value={form.program_code} onChange={(e) => setForm((p) => ({ ...p, program_code: e.target.value }))} disabled={Boolean(form.id)} /></Grid>
            <Grid item xs={12} md={8}><TextField label="Title" fullWidth value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} /></Grid>
            <Grid item xs={12}><TextField label="Subtitle" fullWidth value={form.subtitle} onChange={(e) => setForm((p) => ({ ...p, subtitle: e.target.value }))} /></Grid>
            <Grid item xs={12}><TextField label="Description" fullWidth multiline minRows={2} value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} /></Grid>
            <Grid item xs={12} md={4}><TextField label="Category" fullWidth value={form.category} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))} /></Grid>
            <Grid item xs={12} md={4}><TextField label="Mode" fullWidth value={form.delivery_mode} onChange={(e) => setForm((p) => ({ ...p, delivery_mode: e.target.value }))} /></Grid>
            <Grid item xs={12} md={4}><TextField label="Level" fullWidth value={form.level} onChange={(e) => setForm((p) => ({ ...p, level: e.target.value }))} /></Grid>
            <Grid item xs={12} md={3}><TextField type="number" label="Duration Days" fullWidth value={form.duration_days} onChange={(e) => setForm((p) => ({ ...p, duration_days: e.target.value }))} /></Grid>
            <Grid item xs={12} md={3}><TextField type="number" label="Fee" fullWidth value={form.fee} onChange={(e) => setForm((p) => ({ ...p, fee: e.target.value }))} /></Grid>
            <Grid item xs={12} md={3}><TextField type="number" label="Seats" fullWidth value={form.seats} onChange={(e) => setForm((p) => ({ ...p, seats: e.target.value }))} /></Grid>
            <Grid item xs={12} md={3}><TextField type="number" label="Rating" fullWidth value={form.rating} onChange={(e) => setForm((p) => ({ ...p, rating: e.target.value }))} /></Grid>
            <Grid item xs={12} md={6}><TextField label="Language" fullWidth value={form.language} onChange={(e) => setForm((p) => ({ ...p, language: e.target.value }))} /></Grid>
            <Grid item xs={12} md={3}><TextField label="Status" fullWidth value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))} /></Grid>
            <Grid item xs={12} md={3}><TextField type="number" label="Display Order" fullWidth value={form.display_order} onChange={(e) => setForm((p) => ({ ...p, display_order: e.target.value }))} /></Grid>
            <Grid item xs={12} md={3}>
              <Stack direction="row" alignItems="center" sx={{ mt: 1 }}>
                <Typography variant="body2">Featured</Typography>
                <Switch checked={Boolean(form.is_featured)} onChange={(e) => setForm((p) => ({ ...p, is_featured: e.target.checked }))} />
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}><TextField label="Tags (one per line)" fullWidth multiline minRows={4} value={form.tags} onChange={(e) => setForm((p) => ({ ...p, tags: e.target.value }))} /></Grid>
            <Grid item xs={12} md={4}><TextField label="Highlights (one per line)" fullWidth multiline minRows={4} value={form.highlights} onChange={(e) => setForm((p) => ({ ...p, highlights: e.target.value }))} /></Grid>
            <Grid item xs={12} md={4}><TextField label="Syllabus (one per line)" fullWidth multiline minRows={4} value={form.syllabus} onChange={(e) => setForm((p) => ({ ...p, syllabus: e.target.value }))} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveProgram}>Save</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TrainingProgramsManagement;
